import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Alert, Spin } from 'antd';
import {
  AlertOutlined,
  DollarOutlined,
  ToolOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';

const fmt = (n) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(n || 0);

const ascensorColumns = [
  { title: 'Ascensor', dataIndex: 'nombre', key: 'nombre' },
  {
    title: 'Marca',
    dataIndex: 'marca',
    key: 'marca',
    render: (v) => v || '—',
  },
  {
    title: 'Mantenciones',
    dataIndex: 'mantenciones',
    key: 'mantenciones',
    sorter: (a, b) => a.mantenciones - b.mantenciones,
  },
  {
    title: '% Falla',
    dataIndex: 'pct_falla',
    key: 'pct_falla',
    render: (v) => (
      <Tag color={v >= 25 ? 'red' : v >= 15 ? 'orange' : 'green'}>{v}%</Tag>
    ),
    sorter: (a, b) => a.pct_falla - b.pct_falla,
    defaultSortOrder: 'descend',
  },
];

function SaludPage({ id }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    axios
      .get(`${API_PATH}/kpi/cliente/${id}/salud`, {
        headers: { Authorization: getAccessTokenApi() },
      })
      .then((res) => {
        if (!cancel) {
          setData(res.data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancel) setError(err);
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert
        type="error"
        message="No se pudo cargar la salud del cliente"
        description={error?.message}
      />
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Mantenciones (12m)"
              value={data.mantenciones.total}
              prefix={<ToolOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
              {data.mantenciones.enviadas} enviadas ·{' '}
              {data.mantenciones.finalizadas} finalizadas
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Emergencias (12m)"
              value={data.emergencias_12m}
              prefix={<AlertOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Hallazgos activos"
              value={
                typeof data.hallazgos_activos === 'object'
                  ? data.hallazgos_activos.total
                  : data.hallazgos_activos
              }
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
            {data.hallazgos_activos?.criticos > 0 && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#f5222d',
                }}
              >
                {data.hallazgos_activos.criticos} críticos (alta + crítica)
              </div>
            )}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Presupuestos abiertos"
              value={fmt(data.pipeline.monto_total)}
              suffix={`(${data.pipeline.total_abiertos})`}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#faad14', fontSize: '1.2rem' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Ascensores del cliente y tasa de falla preventiva (12m)"
        style={{ marginTop: 16 }}
      >
        <Table
          dataSource={data.ascensores}
          columns={ascensorColumns}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
}

export default SaludPage;
